// sitrepProgressState.js — MINSKY-16410: pure derivation from a `sitrepGenerations` entry (as
// returned by `getSitrepGeneration`, see sitrepGenerationsYMap.js) to the small view-state the
// under-the-map progress UI (SitrepGenerationProgress.jsx) and its host (MapNodeView.js) need —
// kept free of Yjs/DOM/React so the mapping itself is trivially unit-testable, mirroring how
// agenticSectionState.js's status→view-state helpers (getAgenticSectionViewState et al.) are
// pure functions the AgenticSectionControls.jsx component consumes.
//
// `phase` intentionally has its own small enum rather than reusing SITREP_GENERATION_STATUS
// directly, since "no generation in flight yet" (idle) and "unrecognized status" both need a
// defined phase that Yjs's status field never actually stores.

import { SITREP_GENERATION_STATUS } from './sitrepGenerationsYMap';

export const SITREP_PROGRESS_PHASE = Object.freeze({
    IDLE: 'idle',
    QUEUED: 'queued',
    GENERATING: 'generating',
    DONE: 'done',
    ERROR: 'error',
});

const DEFAULT_ERROR_MESSAGE = 'Failed to generate situation report.';

const IDLE_STATE = Object.freeze({
    phase: SITREP_PROGRESS_PHASE.IDLE,
    isActive: false,
    isTerminal: false,
    label: '',
    errorMessage: '',
});

/**
 * @param {{status: string, errorMessage?: string}|null} generation  Result of
 *   `getSitrepGeneration(yMapGenerations, requestId)`, or `null` when no request is tracked.
 * @returns {{phase: string, isActive: boolean, isTerminal: boolean, label: string, errorMessage: string}}
 */
export function getSitrepProgressViewState(generation) {
    const status = generation?.status;

    switch (status) {
        case SITREP_GENERATION_STATUS.QUEUED:
            return {
                phase: SITREP_PROGRESS_PHASE.QUEUED,
                isActive: true,
                isTerminal: false,
                label: 'Queued…',
                errorMessage: '',
            };
        case SITREP_GENERATION_STATUS.GENERATING:
            return {
                phase: SITREP_PROGRESS_PHASE.GENERATING,
                isActive: true,
                isTerminal: false,
                label: 'Generating situation report…',
                errorMessage: '',
            };
        case SITREP_GENERATION_STATUS.DONE:
            return {
                phase: SITREP_PROGRESS_PHASE.DONE,
                isActive: false,
                isTerminal: true,
                label: '',
                errorMessage: '',
            };
        case SITREP_GENERATION_STATUS.ERROR:
            return {
                phase: SITREP_PROGRESS_PHASE.ERROR,
                isActive: false,
                isTerminal: true,
                label: '',
                errorMessage: generation?.errorMessage || DEFAULT_ERROR_MESSAGE,
            };
        default:
            return IDLE_STATE;
    }
}
